import { Box, Flex, useTheme } from "@chakra-ui/react";
import React, { ChangeEventHandler } from "react";
import { IoChevronDownSharp } from "react-icons/io5";

export type PillSelectOptions = Array<{
  key: string;
  displayTitle: string;
  count?: number | undefined;
}>;

interface PillSelectProps {
  options: PillSelectOptions;
  selectedOptionKey?: string;
  fallbackText?: string;
  onSelect: ChangeEventHandler<HTMLSelectElement>;
  greenBG?: boolean;
}

export const PillSelect: React.FC<PillSelectProps> = ({
  options,
  selectedOptionKey,
  fallbackText = "No position found",
  onSelect,
  greenBG = false,
}) => {
  const { colors } = useTheme();
  const selectedOption = options.find((o) => o.key === selectedOptionKey);
  const interactive = options.length > 1;
  const hover = interactive
    ? { background: greenBG ? "blue.50" : "gray.50" }
    : {};

  const displayText = selectedOption
    ? selectedOption.displayTitle
    : fallbackText;

  const marginLeft = greenBG ? { base: 6, md: 6 } : { base: 12, md: 12 };
  const marginRight = greenBG
    ? { base: interactive ? 10 : 4, md: interactive ? 10 : 6 }
    : { base: interactive ? 16 : 10, md: interactive ? 16 : 12 };

  const background = greenBG ? (interactive ? "white" : "gray.100") : "white";
  const borderColor = greenBG
    ? interactive
      ? "gray.200"
      : "gray.100"
    : "border";

  return (
    <Box
      position="relative"
      h={10}
      w="100%"
      bg={background}
      borderRadius="full"
      border={greenBG ? "2px solid" : "1px solid"}
      borderColor={borderColor}
      boxShadow={!greenBG || interactive ? "tight" : undefined}
      transition="background 0.1s"
      _hover={hover}
    >
      <Flex
        h="100%"
        maxW="100%"
        justifyContent={{ base: "left", md: "center" }}
        alignItems="center"
        ml={marginLeft}
        mr={marginRight}
        pointerEvents="none"
        whiteSpace="nowrap"
        overflow="hidden"
        color={greenBG ? colors.gray[900] : undefined}
      >
        {displayText}
        {interactive && (
          <Box position="absolute" right={4}>
            <IoChevronDownSharp size="16px" color="gray.500" />
          </Box>
        )}
      </Flex>
      {interactive && (
        <select
          onChange={onSelect}
          value={selectedOptionKey}
          style={{
            width: "100%",
            position: "absolute",
            top: "0",
            bottom: "0",
            left: "0",
            right: "0",
            opacity: "0",
            cursor: "pointer",
          }}
        >
          {options.map((o) => (
            <option key={o.key} value={o.key}>
              {`${o.displayTitle}${
                o.count === undefined ? "" : ` (${o.count})`
              }`}
            </option>
          ))}
        </select>
      )}
    </Box>
  );
};

export default PillSelect;
